import {Icon, IconData, IconInterface} from "./Icon";
import {Media, MediaData, MediaInterface} from "./Media";
import {MediaVariant, MediaVariantData, MediaVariantInterface} from "./MediaVariant";
import {BreadcrumbData, Breadcrumbs, BreadcrumbsInterface} from "./Breadcrumb";
import Thesis, {ThesisData, ThesisInterface} from "./Thesis";
import {CommentableInterface, RatingableInterface, SocialShareableInterface} from "./interfaces";
import {Store, useStore} from "vuex";
import {RatingMetricsInterface} from "./rating/RatingMetrics";

export declare type ItemAttrs = {
  [k: string]: string
}

export interface PageHeadData {
  type: string
  language_tid: string
  charset?: string
  title: string
  description: string
  keywords?: string
  robots?: string
  canonical?: ItemAttrs
  next_prev?: boolean
  meta?: Array<ItemAttrs>
  twitter?: ItemAttrs
  og?: ItemAttrs
  link?: Array<ItemAttrs>
}

export declare type HeadData = {
  htmlAttrs: { lang: string, amp?: boolean }
  bodyAttrs?: ItemAttrs
  title: string
  meta: Array<ItemAttrs>
  link: Array<ItemAttrs>
  base?: ItemAttrs
}


export interface PageLinkData {
  link: string,
  title: string | null | undefined,
}

export interface PageLinkInterface {
  link(): string,

  title(): string | null | undefined,
}

export class PageLink implements PageLinkInterface {
  private data: PageLinkData

  constructor(data: PageLinkData) {
    this.data = data
  }

  public link(): string {
    return this.data.link
  }

  public title(): string | null | undefined {
    return this.data.title
  }
}

export interface PageTextData {
  id: number
  content: string
}
export interface PageContentData {
  id: number
  type_tid: string
  sort_num: number
  lazy: boolean
  header: string | null | undefined
  more: PageLinkData | null | undefined
  content: string | PageTextData | undefined
}

export interface PageContentInterface {
  id(): number | null

  type(): string

  sortNum(): number

  lazy(): boolean

  header(): string | null | undefined

  more(): PageLinkInterface | null

  content(): string | PageTextData | undefined
}

class PageContent implements PageContentInterface {
  private data: PageContentData
  private readonly _more: PageLinkInterface | null

  constructor(data: PageContentData) {
    this.data = data
    this._more = (typeof data.more === 'object' && data.more !== null)
      ? new PageLink(data.more as PageLinkData)
      : null
  }

  id(): number {
    return this.data.id
  }

  type(): string {
    return this.data.type_tid
  }

  sortNum(): number {
    return this.data.sort_num
  }

  lazy(): boolean {
    return this.data.lazy
  }

  header(): string | null | undefined {
    return this.data.header
  }

  more(): PageLinkInterface | null {
    return this._more
  }

  content(): string | PageTextData | undefined {
    return this.data.content
  }
}

export interface PageData {
  id: number
  tid: string
  adult: boolean
  age_rating: number
  allow_comment: boolean
  comment_count: number

  icon: IconData | null
  icon_color: string | null

  view_count: number
  voice_count: number
  voice_sum: number
  voice_average: number

  header: string
  sub_header: string | null
  description: string
  introduction: string | null
  sub_menu_tid: string | null

  heads: Array<PageHeadData>
  breadcrumbs: Array<BreadcrumbData>
  content: Array<PageContentData>
  theses: Array<ThesisData>

  image: MediaData | null,
  image_variants: Array<MediaVariantData>
  published_at: string | null,
  updated_at: string | null,

  path: string,
}

export const pageEmptyData: PageData = {
  id: 0,
  tid: '',
  adult: false,
  age_rating: 0,
  allow_comment: true,
  comment_count: 0,

  icon: null,
  icon_color: null,

  view_count: 0,
  voice_count: 0,
  voice_sum: 0,
  voice_average: 0,

  header: '',
  sub_header: null,
  description: '',
  introduction: '',
  sub_menu_tid: null,

  heads: [] as Array<PageHeadData>,
  breadcrumbs: [] as Array<BreadcrumbData>,
  content: [] as Array<PageContentData>,
  theses: [] as Array<ThesisData>,

  image: null,
  image_variants: [] as Array<MediaVariantData>,

  published_at: null,
  updated_at: null,

  path: '',
}

export interface PageInterface extends SocialShareableInterface, RatingableInterface, CommentableInterface {
  id(): number

  tid(): string

  header(): string

  subHeader(): string | null

  hasTheses(): boolean

  theses(): Array<ThesisInterface>

  icon(): IconInterface

  iconColor(): string | null

  introduction(): string

  subMenuTid(): string | null

  image(): MediaInterface | null

  hasImageVariants(): boolean

  imageVariants(): Array<MediaVariantInterface>

  content(): Array<PageContentInterface>

  description(): string

  publishedAt(): Date | null,

  publishedAtIso(): string

  publishedAtLocaleDateString(locale: string): string

  updatedAt(): Date | null

  updatedAtIso(): string

  updatedAtLocaleDateString(locale: string): string

  allowComment(): boolean

  adult(): boolean

  ageRating(): number

  countComments(): number

  countViews(): number

  countVoices(): number

  sumVoices(): number

  averageVoices(): number

  heads(): Array<PageHeadData>

  breadcrumbs(): BreadcrumbsInterface

  path(): string
}

export class Page implements PageInterface {

  private data: PageData
  private readonly _theses: Array<ThesisInterface>
  private readonly _icon: Icon
  private readonly _image: Media | null
  private readonly _image_variants: Array<MediaVariantInterface>
  private readonly _publishedAt: Date | null
  private readonly _updatedAt: Date | null

  private readonly _breadcrumbs: BreadcrumbsInterface
  private readonly _content: Array<PageContentInterface>

  public constructor(data: PageData) {
    this.data = data

    this._theses = []
    for (let thesis of data.theses) {
      this._theses.push(new Thesis(thesis as ThesisData))
    }

    this._icon = data.icon ? new Icon(data.icon as IconData) : new Icon({} as IconData)
    this._image = data.image ? new Media(data.image as MediaData) : null;
    this._publishedAt = data.published_at ? new Date(data.published_at) : null
    this._updatedAt = data.updated_at ? new Date(data.updated_at) : null
    this._image_variants = []
    for (let imgVariant of data.image_variants) {
      this._image_variants.push(new MediaVariant(imgVariant))
    }

    this._breadcrumbs = new Breadcrumbs(data.breadcrumbs)

    this._content = [];
    for (let c of data.content) {
      this._content.push(new PageContent(c as PageContentData))
    }
  }

  public id(): number {
    return this.data.id;
  }

  public tid(): string {
    return this.data.tid;
  }

  public header(): string {
    return this.data.header ?? ''
  }

  public subHeader(): string | null {
    return this.data.sub_header
  }

  public hasTheses(): boolean {
    return this._theses.length > 0
  }

  public theses(): Array<ThesisInterface> {
    return this._theses
  }

  public icon(): IconInterface {
    return this._icon
  }

  public iconColor(): string | null {
    return this.data.icon_color
  }

  public introduction(): string {
    return this.data.introduction ?? ''
  }

  subMenuTid(): string | null {
    return this.data.sub_menu_tid
  }

  public image(): MediaInterface | null {
    return this._image
  }

  public hasImageVariants(): boolean {
    return this._image_variants.length > 0
  }

  public imageVariants(): Array<MediaVariantInterface> {
    return this._image_variants
  }

  public content(): Array<PageContentInterface> {
    return this._content??[]
  }

  public description(): string {
    return this.data.description ?? ''
  }

  public info(maxLen: number): string {
    return (this.data.description ?? this.data.introduction ?? '').substring(0, maxLen > 1 ? maxLen : 200)
  }

  public publishedAt(): Date | null {
    return this._publishedAt
  }

  public publishedAtIso(): string {
    return this._publishedAt
      ? this._publishedAt.toISOString()
      : ''
  }

  public publishedAtLocaleDateString(locale: string = 'ru'): string {
    return this._publishedAt
      ? this._publishedAt.toLocaleDateString(locale)
      : ''
  }

  public updatedAt(): Date | null {
    return this._updatedAt
  }

  public updatedAtIso(): string {
    return this._updatedAt
      ? this._updatedAt.toISOString()
      : ''
  }

  public updatedAtLocaleDateString(locale: string = 'ru'): string {
    return this._updatedAt
      ? this._updatedAt.toLocaleDateString(locale)
      : ''
  }

  public allowComment(): boolean {
    return this.data.allow_comment
  }

  public adult(): boolean {
    return this.data.adult
  }

  public ageRating(): number {
    return this.data.age_rating
  }

  public countComments(): number {
    return this.data.comment_count
  }

  public countViews(): number {
    return this.data.view_count
  }

  public countVoices(): number {
    return this.data.voice_count
  }

  public sumVoices(): number {
    return this.data.voice_sum
  }

  public averageVoices(): number {
    return this.data.voice_average
  }

  setVoicesMetric(r: RatingMetricsInterface): void {
    this.data.voice_count = r.count()
    this.data.voice_sum = r.sum()
    this.data.voice_average = r.average()
  }

  public async updateStoreVoicesMetric(store: Store<unknown>, r: RatingMetricsInterface): Promise<void> {
    return store.commit('pages/pageDataUpdateRating', {path: this.path(), r:r})
  }

  public heads(): Array<PageHeadData> {
    return this.data.heads
  }

  public breadcrumbs(): BreadcrumbsInterface {
    return this._breadcrumbs
  }

  public path(): string {
    return this.data.path
  }
}
